import React, { useEffect, useState, useRef } from "react";
import { HashLink } from "react-router-hash-link";
import axios from "axios";
import "../../../assets/styles/Navbar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_DOMAIN } from "../../../common/env";
import { Box } from "@mui/material";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // For mobile menu toggle
  const dropdownRef = useRef(null);
  const avatarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    // Check if user is admin based on role in localStorage
    const checkAdminStatus = () => {
      const role = localStorage.getItem('role');
      const token = localStorage.getItem('token');
      setIsAdmin(token && (role === 'admin' || role === 'owner'));
    };

    window.addEventListener("scroll", handleScroll);
    checkAdminStatus();
    
    // Listen for storage changes to update admin status
    window.addEventListener('storage', checkAdminStatus);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener('storage', checkAdminStatus);
    };
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      axios
        .get(`${BACKEND_DOMAIN}/my-details`, {
          headers: {
            Authorization: `Bearer ${storedToken}`,
          },
        })
        .then((response) => {
          const userData = response.data;
          setUser(userData);
          setAvatarUrl(
            `https://ui-avatars.com/api/?name=${userData.username}&background=random&size=128`
          );
        })
        .catch((error) => {
          console.error("Error fetching user details:", error);
        });
    }
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    setUser(null);
    setIsAdmin(false);
    setDropdownVisible(false);
    navigate('/');
  };

  const toggleDropdown = () => {
    console.log("clicking the dropdown")
    setDropdownVisible((prevState) => !prevState);
  };

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close mobile menu when clicking on a link
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        avatarRef.current && !avatarRef.current.contains(event.target)
      ) {
        console.log("closing the dropdown");
        setDropdownVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <nav
  className={`navbar navbar-expand-lg navbar-light ${isScrolled ? "scrolled" : ""} fixed-top`}
>
  <div className="container">
    <div className="d-flex align-items-center">
      {/* Hamburger Menu */}
      <button
        className="navbar-toggler me-2"
        type="button"
        onClick={toggleMenu}
        aria-controls="navbarNav"
        aria-expanded={isMenuOpen}
        aria-label="Toggle navigation"
      >
        <span className="navbar-toggler-icon"></span>
      </button>
      {/* Logo with Trophy */}
      <HashLink className="navbar-brand" to="/#" onClick={closeMenu}>
        🏆 MyLiveAuction
      </HashLink>
    </div>
    <div className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} id="navbarNav">
      <ul className="navbar-nav mx-auto align-items-center">
        <li className="nav-item">
          <HashLink 
            className="nav-link live-auctions-link" 
            to="/auctions" 
            onClick={closeMenu}
          >
            Live Auctions
          </HashLink>
        </li>
        <li className={`nav-item ${location.pathname === '/about' ? 'active' : ''}`}>
          <HashLink 
            className={location.pathname.includes('/live-auction/') ? 'nav-link no-highlight' : 'nav-link'} 
            to="/about" 
            onClick={closeMenu}
          >
            About
          </HashLink>
        </li>
        <li className={`nav-item ${location.pathname === '/how-to-use' ? 'active' : ''}`}>
          <HashLink 
            className={location.pathname.includes('/live-auction/') ? 'nav-link no-highlight' : 'nav-link'} 
            to="/how-to-use" 
            onClick={closeMenu}
          >
            How to Use
          </HashLink>
        </li>
        <li className="nav-item">
          <HashLink 
            className={location.pathname.includes('/live-auction/') ? 'nav-link no-highlight' : 'nav-link'} 
            smooth 
            to="/#features" 
            onClick={closeMenu}
          >
            Features
          </HashLink>
        </li>
        <li className="nav-item">
          <HashLink 
            className={location.pathname.includes('/live-auction/') ? 'nav-link no-highlight' : 'nav-link'} 
            smooth 
            to="/#faqs" 
            onClick={closeMenu}
          >
            FAQs
          </HashLink>
        </li>
        <li className="nav-item">
          <HashLink 
            className={location.pathname.includes('/live-auction/') ? 'nav-link no-highlight' : 'nav-link'} 
            smooth 
            to="/#footer" 
            onClick={closeMenu}
          >
            Contact Us
          </HashLink>
        </li>
      </ul>
      <ul className="navbar-nav align-items-center">
        {user ? (
          <li
            className="nav-item avatar-item dropdown-container"
            style={{ position: "relative", cursor: "pointer" }}
          >
          
            <div ref={avatarRef} onClick={toggleDropdown}>
              <img
                src={avatarUrl}
                alt="User Avatar"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            </div>

            {dropdownVisible && (
              <div
                ref={dropdownRef}
                className="dropdown-menu"
                style={{
                  position: "absolute",
                  top: "45px",
                  right: "0",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                  zIndex: 1500, // Highest z-index to appear above all overlays
                  display: "block", // Force visibility
                }}
              >
                  <>
                    <button 
                      className="dropdown-item admin-dashboard" 
                      onClick={() => {
                        navigate('/admin-dashboard');
                        setDropdownVisible(false);
                        closeMenu();
                      }}
                    >
                      ⚙️ Admin Dashboard
                    </button>
                    <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                  </>
                <button className="dropdown-item" onClick={() => {
                  handleSignOut();
                  closeMenu();
                }}>
                  🚪 Sign Out
                </button>
              </div>
            )}
          </li>
        ) : (
          <>
            <li className="nav-item">
              <HashLink className="nav-link btn btn-outline-primary" to="/login" onClick={closeMenu}>
                Sign In
              </HashLink>
            </li>
          </>
        )}
        {/* Show Create Auction only for admin users */}
        {user && (
          <li className="nav-item">
            <button
              className="nav-link btn-create-auction"
              onClick={() => {
                navigate('/admin-dashboard?create=true');
                closeMenu();
              }}
              style={{ border: 'none' }}
            >
              Create Auction
            </button>
          </li>
        )}
      </ul>
    </div>
  </div>
</nav>
  );
};

export default Navbar;